import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const styles = {
  global: `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif; background: linear-gradient(135deg, #e8f0e9 0%, #d4e5d8 50%, #c5dde0 100%); min-height: 100vh; color: #3d4f4f; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
    button:hover:not(:disabled) { transform: translateY(-2px); }
    button:active:not(:disabled) { transform: translateY(0); }
  `,
  
  // Navigation
  nav: {
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(124, 152, 133, 0.15)',
    padding: '0 40px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  navInner: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '70px',
  },
  navLogo: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#3d4f4f',
    textDecoration: 'none',
  },
  navLinks: {
    display: 'flex',
    gap: '8px',
  },
  navLink: {
    padding: '10px 20px',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '500',
    color: '#5a7070',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    background: 'transparent',
  },
  navLinkActive: {
    background: 'rgba(124, 152, 133, 0.15)',
    color: '#3d4f4f',
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: '600',
    fontSize: '16px',
  },
  userName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#3d4f4f',
  },
  logoutButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(124, 152, 133, 0.3)',
    background: 'transparent',
    color: '#5a7070',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  
  // Page content
  pageContainer: {
    minHeight: 'calc(100vh - 70px)',
    padding: '40px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  pageHeader: {
    marginBottom: '32px',
  },
  pageTitle: {
    fontSize: '32px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '8px',
  },
  pageSubtitle: {
    fontSize: '16px',
    color: '#6b8080',
  },
  
  // Stats bar
  statsBar: {
    display: 'flex',
    gap: '20px',
    marginBottom: '32px',
  },
  statCard: {
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    padding: '20px 24px',
    flex: 1,
    boxShadow: '0 4px 16px rgba(61, 79, 79, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#3d4f4f',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '14px',
    color: '#6b8080',
  },
  
  // Filters
  filtersBar: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1,
    minWidth: '250px',
    padding: '12px 16px',
    paddingLeft: '44px',
    borderRadius: '12px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    background: 'rgba(255, 255, 255, 0.9)',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    minWidth: '250px',
  },
  searchIcon: {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '18px',
    color: '#6b8080',
  },
  filterSelect: {
    padding: '12px 16px',
    borderRadius: '12px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    background: 'rgba(255, 255, 255, 0.9)',
    fontSize: '15px',
    outline: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    minWidth: '150px',
  },
  
  // Candidates grid
  candidatesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '20px',
  },
  
  // Candidate card
  candidateCard: {
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(61, 79, 79, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    animation: 'fadeIn 0.4s ease-out',
  },
  candidateCardHover: {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 32px rgba(61, 79, 79, 0.12)',
  },
  candidateHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '16px',
  },
  candidateAvatar: {
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #93b4ba 0%, #7c9885 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: '600',
    fontSize: '22px',
    flexShrink: 0,
  },
  candidateInfo: {
    flex: 1,
    minWidth: 0,
  },
  candidateName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '4px',
  },
  candidateEmail: {
    fontSize: '13px',
    color: '#6b8080',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  candidateDate: {
    fontSize: '12px',
    color: '#9ab0a5',
    marginTop: '4px',
  },
  
  // Label
  labelContainer: {
    marginBottom: '16px',
  },
  labelBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500',
  },
  labelBadgeDefault: {
    background: 'rgba(124, 152, 133, 0.15)',
    color: '#5a7a65',
  },
  labelBadgeGreen: {
    background: 'rgba(34, 197, 94, 0.15)',
    color: '#166534',
  },
  labelBadgeYellow: {
    background: 'rgba(234, 179, 8, 0.15)',
    color: '#854d0e',
  },
  labelBadgeRed: {
    background: 'rgba(239, 68, 68, 0.15)',
    color: '#b91c1c',
  },
  labelBadgeBlue: {
    background: 'rgba(59, 130, 246, 0.15)',
    color: '#1d4ed8',
  },
  labelBadgePurple: {
    background: 'rgba(147, 51, 234, 0.15)',
    color: '#7e22ce',
  },
  labelBadgeGray: {
    background: 'rgba(107, 114, 128, 0.15)',
    color: '#374151',
  },
  noLabel: {
    fontSize: '13px',
    color: '#9ab0a5',
    fontStyle: 'italic',
  },
  
  // CV Summary
  cvSummary: {
    background: 'rgba(124, 152, 133, 0.08)',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '16px',
  },
  cvSummaryTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#5a7070',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cvSummaryText: {
    fontSize: '14px',
    color: '#3d4f4f',
    lineHeight: '1.5',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  
  // Actions
  cardActions: {
    display: 'flex',
    gap: '10px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(124, 152, 133, 0.1)',
  },
  actionButton: {
    flex: 1,
    padding: '10px 16px',
    borderRadius: '10px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  viewCvButton: {
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    color: 'white',
  },
  editLabelButton: {
    background: 'rgba(124, 152, 133, 0.15)',
    color: '#5a7a65',
  },
  rejectButton: {
    background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
    color: 'white',
  },
  
  // Rejection Template Section
  templateSection: {
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '32px',
    boxShadow: '0 4px 20px rgba(61, 79, 79, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
  },
  templateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  templateTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3d4f4f',
  },
  templateToggle: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(124, 152, 133, 0.15)',
    color: '#5a7a65',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  templateHelp: {
    fontSize: '13px',
    color: '#6b8080',
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  variablesList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '16px',
  },
  variableTag: {
    padding: '4px 10px',
    borderRadius: '6px',
    background: 'rgba(124, 152, 133, 0.1)',
    color: '#5a7a65',
    fontSize: '12px',
    fontFamily: 'monospace',
  },
  
  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
    animation: 'fadeIn 0.3s ease-out',
  },
  modalHeader: {
    padding: '24px',
    borderBottom: '1px solid rgba(124, 152, 133, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#3d4f4f',
  },
  closeButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: 'none',
    background: 'rgba(124, 152, 133, 0.1)',
    cursor: 'pointer',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#5a7070',
    transition: 'all 0.2s ease',
  },
  modalBody: {
    padding: '24px',
    maxHeight: 'calc(90vh - 160px)',
    overflowY: 'auto',
  },
  modalFooter: {
    padding: '20px 24px',
    borderTop: '1px solid rgba(124, 152, 133, 0.15)',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  
  // Form elements
  formGroup: {
    marginBottom: '20px',
  },
  formLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#5a7070',
    marginBottom: '8px',
  },
  formInput: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  formTextarea: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    fontSize: '15px',
    outline: 'none',
    resize: 'vertical',
    minHeight: '100px',
    fontFamily: 'inherit',
  },
  
  // Preset labels
  presetLabels: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
  },
  presetLabelButton: {
    padding: '8px 14px',
    borderRadius: '20px',
    border: '2px solid transparent',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  
  // CV Viewer
  cvViewer: {
    width: '100%',
    height: '70vh',
    border: 'none',
    borderRadius: '12px',
    background: '#f5f5f5',
  },
  
  // Empty state
  emptyState: {
    textAlign: 'center',
    padding: '60px 40px',
    background: 'rgba(255, 255, 255, 0.85)',
    borderRadius: '24px',
    boxShadow: '0 4px 20px rgba(61, 79, 79, 0.08)',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '16px',
    color: '#6b8080',
    marginBottom: '24px',
  },
  emptyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 28px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    color: 'white',
    fontSize: '16px',
    fontWeight: '600',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
  },
};

const presetLabels = [
  { text: 'Strong Candidate', color: 'green' },
  { text: 'Maybe', color: 'yellow' },
  { text: 'Not a Fit', color: 'red' },
  { text: 'Schedule Follow-up', color: 'blue' },
  { text: 'Offer Extended', color: 'purple' },
];

const colorOptions = [
  { value: 'green', label: 'Green', bg: 'rgba(34, 197, 94, 0.15)', text: '#166534' },
  { value: 'yellow', label: 'Yellow', bg: 'rgba(234, 179, 8, 0.15)', text: '#854d0e' },
  { value: 'red', label: 'Red', bg: 'rgba(239, 68, 68, 0.15)', text: '#b91c1c' },
  { value: 'blue', label: 'Blue', bg: 'rgba(59, 130, 246, 0.15)', text: '#1d4ed8' },
  { value: 'purple', label: 'Purple', bg: 'rgba(147, 51, 234, 0.15)', text: '#7e22ce' },
  { value: 'gray', label: 'Gray', bg: 'rgba(107, 114, 128, 0.15)', text: '#374151' },
];

function CandidatesPage({ user, setUser }) {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [customLabels, setCustomLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLabel, setFilterLabel] = useState('all');
  const [hoveredCard, setHoveredCard] = useState(null);
  
  // Modal states
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showCvModal, setShowCvModal] = useState(false);
  const [showCreateLabelModal, setShowCreateLabelModal] = useState(false);
  const [editLabel, setEditLabel] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [cvUrl, setCvUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // New label creation
  const [newLabelText, setNewLabelText] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('gray');
  const [creatingLabel, setCreatingLabel] = useState(false);

  // Rejection template states
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [rejectionSubject, setRejectionSubject] = useState('Update on your application');
  const [rejectionBody, setRejectionBody] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingCandidate, setRejectingCandidate] = useState(null);
  const [sendingRejection, setSendingRejection] = useState(false);

  useEffect(() => {
    if (user?.role !== 'hiring_manager') {
      navigate('/home');
      return;
    }
    fetchCandidates();
    fetchRejectionTemplate();
  }, [user, navigate]);

  const fetchCandidates = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/candidates`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCandidates(data.data.candidates);
        setCustomLabels(data.data.customLabels || []);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRejectionTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/rejection-template`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success && data.data.template) {
        setRejectionSubject(data.data.template.subject || 'Update on your application');
        setRejectionBody(data.data.template.body || '');
      }
    } catch (error) {
      console.error('Error fetching rejection template:', error);
    }
  };

  const saveRejectionTemplate = async () => {
    setSavingTemplate(true);
    try {
      const response = await fetch(`${API_URL}/auth/rejection-template`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          subject: rejectionSubject,
          body: rejectionBody
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('Rejection template saved!');
      } else {
        alert(data.message || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const openRejectModal = (candidate, e) => {
    e.stopPropagation();
    if (!rejectionBody) {
      alert('Please set up a rejection email template first');
      setShowTemplateEditor(true);
      return;
    }
    setRejectingCandidate(candidate);
    setShowRejectModal(true);
  };

  const sendRejectionEmail = async () => {
    if (!rejectingCandidate) return;
    
    setSendingRejection(true);
    try {
      const response = await fetch(`${API_URL}/auth/reject-candidate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          odejId: rejectingCandidate.odejId,
          jobListingId: rejectingCandidate.jobListingId
        })
      });
      const data = await response.json();
      if (data.success) {
        alert(`Rejection email sent to ${data.data.candidateName}`);
        // Update local state to show "Rejected" label
        setCandidates(prev => prev.map(c =>
          c.odejId === rejectingCandidate.odejId && c.jobListingId === rejectingCandidate.jobListingId
            ? { ...c, label: 'Rejected' }
            : c
        ));
        setShowRejectModal(false);
        setRejectingCandidate(null);
      } else {
        alert(data.message || 'Failed to send rejection email');
      }
    } catch (error) {
      console.error('Error sending rejection:', error);
      alert('Failed to send rejection email');
    } finally {
      setSendingRejection(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const openLabelModal = (candidate, e) => {
    e.stopPropagation();
    setSelectedCandidate(candidate);
    setEditLabel(candidate.label || '');
    setEditNotes(candidate.notes || '');
    setShowLabelModal(true);
  };

  const openCvModal = async (candidate, e) => {
    e.stopPropagation();
    setSelectedCandidate(candidate);
    
    // Fetch signed URL for CV
    if (candidate.candidateData?.cvFile?.s3Key) {
      try {
        const response = await fetch(`${API_URL}/auth/cv/${candidate.odejId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        if (data.success) {
          setCvUrl(data.data.url);
        }
      } catch (error) {
        console.error('Error fetching CV URL:', error);
      }
    }
    
    setShowCvModal(true);
  };

  const saveLabelAndNotes = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/auth/candidate-label`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          odejId: selectedCandidate.odejId,
          jobListingId: selectedCandidate.jobListingId,
          label: editLabel,
          notes: editNotes
        })
      });
      
      const data = await response.json();
      if (data.success) {
        // Update local state
        setCandidates(prev => prev.map(c => 
          c.odejId === selectedCandidate.odejId && c.jobListingId === selectedCandidate.jobListingId
            ? { ...c, label: editLabel, notes: editNotes }
            : c
        ));
        setShowLabelModal(false);
      }
    } catch (error) {
      console.error('Error saving label:', error);
    } finally {
      setSaving(false);
    }
  };

  const createCustomLabel = async () => {
    if (!newLabelText.trim()) return;
    
    setCreatingLabel(true);
    try {
      const response = await fetch(`${API_URL}/auth/custom-label`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          text: newLabelText.trim(),
          color: newLabelColor
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setCustomLabels(data.data.customLabels);
        setNewLabelText('');
        setNewLabelColor('gray');
        setShowCreateLabelModal(false);
      } else {
        alert(data.message || 'Failed to create label');
      }
    } catch (error) {
      console.error('Error creating label:', error);
    } finally {
      setCreatingLabel(false);
    }
  };

  const deleteCustomLabel = async (labelText) => {
    if (!window.confirm(`Delete the label "${labelText}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/auth/custom-label/${encodeURIComponent(labelText)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setCustomLabels(data.data.customLabels);
      }
    } catch (error) {
      console.error('Error deleting label:', error);
    }
  };

  const getLabelStyle = (label) => {
    const lowerLabel = label?.toLowerCase() || '';
    
    // Check custom labels first
    const customLabel = customLabels.find(l => l.text.toLowerCase() === lowerLabel);
    if (customLabel) {
      const colorOption = colorOptions.find(c => c.value === customLabel.color);
      if (colorOption) {
        return { background: colorOption.bg, color: colorOption.text };
      }
    }
    
    // Check preset labels
    const presetLabel = presetLabels.find(l => l.text.toLowerCase() === lowerLabel);
    if (presetLabel) {
      const colorOption = colorOptions.find(c => c.value === presetLabel.color);
      if (colorOption) {
        return { background: colorOption.bg, color: colorOption.text };
      }
    }
    
    // Fallback to keyword matching
    if (lowerLabel.includes('strong') || lowerLabel.includes('great') || lowerLabel.includes('hire')) {
      return styles.labelBadgeGreen;
    }
    if (lowerLabel.includes('maybe') || lowerLabel.includes('consider')) {
      return styles.labelBadgeYellow;
    }
    if (lowerLabel.includes('not') || lowerLabel.includes('reject') || lowerLabel.includes('pass')) {
      return styles.labelBadgeRed;
    }
    if (lowerLabel.includes('follow') || lowerLabel.includes('schedule') || lowerLabel.includes('call')) {
      return styles.labelBadgeBlue;
    }
    if (lowerLabel.includes('offer') || lowerLabel.includes('hired')) {
      return styles.labelBadgePurple;
    }
    return styles.labelBadgeDefault;
  };

  const getColorStyle = (color) => {
    const colorOption = colorOptions.find(c => c.value === color);
    return colorOption ? { background: colorOption.bg, color: colorOption.text } : {};
  };

  // Combine preset and custom labels
  const allLabels = [
    ...presetLabels,
    ...customLabels.map(l => ({ text: l.text, color: l.color, isCustom: true }))
  ];

  const filteredCandidates = candidates.filter(candidate => {
    const matchesSearch = 
      candidate.candidateData?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.candidateData?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.candidateData?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.label?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = 
      filterLabel === 'all' ||
      (filterLabel === 'labeled' && candidate.label) ||
      (filterLabel === 'unlabeled' && !candidate.label);
    
    return matchesSearch && matchesFilter;
  });

  const uniqueLabels = [...new Set(candidates.filter(c => c.label).map(c => c.label))];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(124, 152, 133, 0.2)',
          borderTopColor: '#7c9885',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      <style>{styles.global}</style>
      
      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <Link to="/home" style={styles.navLogo}>Instaview</Link>
          
          <div style={styles.navLinks}>
            <Link to="/home" style={styles.navLink}>
              🏠 Home
            </Link>
            <Link to="/candidates" style={{ ...styles.navLink, ...styles.navLinkActive }}>
              👥 Candidates
            </Link>
          </div>
          
          <div style={styles.navRight}>
            <div style={styles.userInfo}>
              <div style={styles.userAvatar}>
                {user?.firstName?.charAt(0).toUpperCase()}
              </div>
              <span style={styles.userName}>{user?.firstName} {user?.lastName}</span>
            </div>
            <button onClick={handleLogout} style={styles.logoutButton}>
              Logout
            </button>
          </div>
        </div>
      </nav>
      
      {/* Page Content */}
      <div style={styles.pageContainer}>
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>Candidates</h1>
          <p style={styles.pageSubtitle}>Manage and review all the candidates you've interviewed</p>
        </div>
        
        {/* Stats */}
        <div style={styles.statsBar}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{candidates.length}</div>
            <div style={styles.statLabel}>Total Interviews</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{candidates.filter(c => c.label).length}</div>
            <div style={styles.statLabel}>Labeled</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{candidates.filter(c => !c.label).length}</div>
            <div style={styles.statLabel}>Needs Review</div>
          </div>
        </div>

        {/* Rejection Email Template Section */}
        <div style={styles.templateSection}>
          <div style={styles.templateHeader}>
            <h2 style={styles.templateTitle}>📧 Rejection Email Template</h2>
            <button
              style={styles.templateToggle}
              onClick={() => setShowTemplateEditor(!showTemplateEditor)}
            >
              {showTemplateEditor ? 'Hide Template' : 'Edit Template'}
            </button>
          </div>
          
          {showTemplateEditor && (
            <>
              <p style={styles.templateHelp}>
                Create a template for rejection emails. Use the variables below to personalize each email:
              </p>
              <div style={styles.variablesList}>
                <span style={styles.variableTag}>{'{candidateFirstName}'}</span>
                <span style={styles.variableTag}>{'{candidateLastName}'}</span>
                <span style={styles.variableTag}>{'{candidateName}'}</span>
                <span style={styles.variableTag}>{'{companyName}'}</span>
                <span style={styles.variableTag}>{'{roleName}'}</span>
                <span style={styles.variableTag}>{'{hiringManagerName}'}</span>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Email Subject</label>
                <input
                  type="text"
                  value={rejectionSubject}
                  onChange={(e) => setRejectionSubject(e.target.value)}
                  placeholder="Update on your application"
                  style={styles.formInput}
                  maxLength={200}
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Email Body</label>
                <textarea
                  value={rejectionBody}
                  onChange={(e) => setRejectionBody(e.target.value)}
                  placeholder={`Dear {candidateFirstName},

Thank you for taking the time to interview with us for the {roleName} position at {companyName}.

After careful consideration, we have decided to move forward with other candidates whose qualifications more closely match our current needs.

We appreciate your interest in our company and wish you the best in your job search.

Best regards,
{hiringManagerName}`}
                  style={{ ...styles.formTextarea, minHeight: '200px' }}
                  maxLength={5000}
                />
              </div>
              
              <button
                onClick={saveRejectionTemplate}
                disabled={savingTemplate || !rejectionBody}
                style={{
                  ...styles.actionButton,
                  ...styles.viewCvButton,
                  opacity: savingTemplate || !rejectionBody ? 0.6 : 1,
                }}
              >
                {savingTemplate ? 'Saving...' : '💾 Save Template'}
              </button>
            </>
          )}
        </div>
        
        {candidates.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>👥</div>
            <h2 style={styles.emptyTitle}>No candidates yet</h2>
            <p style={styles.emptyText}>Start interviewing to see your candidates here</p>
            <Link to="/home" style={styles.emptyButton}>
              🎯 Find Candidates
            </Link>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div style={styles.filtersBar}>
              <div style={styles.searchWrapper}>
                <span style={styles.searchIcon}>🔍</span>
                <input
                  type="text"
                  placeholder="Search by name, email, or label..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={styles.searchInput}
                />
              </div>
              <select
                value={filterLabel}
                onChange={(e) => setFilterLabel(e.target.value)}
                style={styles.filterSelect}
              >
                <option value="all">All Candidates</option>
                <option value="labeled">Labeled</option>
                <option value="unlabeled">Needs Review</option>
              </select>
            </div>
            
            {/* Candidates Grid */}
            <div style={styles.candidatesGrid}>
              {filteredCandidates.map((candidate, index) => (
                <div
                  key={`${candidate.odejId}-${candidate.jobListingId}`}
                  style={{
                    ...styles.candidateCard,
                    ...(hoveredCard === index ? styles.candidateCardHover : {}),
                    animationDelay: `${index * 0.05}s`,
                  }}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div style={styles.candidateHeader}>
                    <div style={styles.candidateAvatar}>
                      {candidate.candidateData?.firstName?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div style={styles.candidateInfo}>
                      <div style={styles.candidateName}>
                        {candidate.candidateData?.firstName} {candidate.candidateData?.lastName}
                      </div>
                      <div style={styles.candidateEmail}>
                        {candidate.candidateData?.email}
                      </div>
                      <div style={styles.candidateDate}>
                        Interviewed: {new Date(candidate.matchedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                  
                  <div style={styles.labelContainer}>
                    {candidate.label ? (
                      <span style={{ ...styles.labelBadge, ...getLabelStyle(candidate.label) }}>
                        🏷️ {candidate.label}
                      </span>
                    ) : (
                      <span style={styles.noLabel}>No label assigned</span>
                    )}
                  </div>
                  
                  {candidate.candidateData?.cv?.summary && (
                    <div style={styles.cvSummary}>
                      <div style={styles.cvSummaryTitle}>Summary</div>
                      <div style={styles.cvSummaryText}>
                        {candidate.candidateData.cv.summary}
                      </div>
                    </div>
                  )}
                  
                  <div style={styles.cardActions}>
                    <button
                      onClick={(e) => openCvModal(candidate, e)}
                      style={{ ...styles.actionButton, ...styles.viewCvButton }}
                      disabled={!candidate.candidateData?.cvFile?.s3Key}
                    >
                      📄 View CV
                    </button>
                    <button
                      onClick={(e) => openLabelModal(candidate, e)}
                      style={{ ...styles.actionButton, ...styles.editLabelButton }}
                    >
                      ✏️ Edit Label
                    </button>
                    <button
                      onClick={(e) => openRejectModal(candidate, e)}
                      style={{ ...styles.actionButton, ...styles.rejectButton }}
                      disabled={candidate.label === 'Rejected'}
                    >
                      {candidate.label === 'Rejected' ? '✓ Rejected' : '✕ Reject'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      
      {/* Label Edit Modal */}
      {showLabelModal && selectedCandidate && (
        <div style={styles.modalOverlay} onClick={() => setShowLabelModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                Edit Label for {selectedCandidate.candidateData?.firstName}
              </h2>
              <button
                style={styles.closeButton}
                onClick={() => setShowLabelModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Label</label>
                <input
                  type="text"
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                  placeholder="Enter a custom label..."
                  style={styles.formInput}
                  maxLength={50}
                />
                
                {/* Preset Labels Section */}
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '12px', color: '#6b8080', marginBottom: '8px', fontWeight: '500' }}>
                    Quick Labels
                  </div>
                  <div style={styles.presetLabels}>
                    {presetLabels.map((preset) => (
                      <button
                        key={preset.text}
                        onClick={() => setEditLabel(preset.text)}
                        style={{
                          ...styles.presetLabelButton,
                          ...getColorStyle(preset.color),
                          borderColor: editLabel === preset.text ? '#3d4f4f' : 'transparent',
                        }}
                      >
                        {preset.text}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Custom Labels Section */}
                {customLabels.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#6b8080', marginBottom: '8px', fontWeight: '500' }}>
                      Your Custom Labels
                    </div>
                    <div style={styles.presetLabels}>
                      {customLabels.map((label) => (
                        <button
                          key={label.text}
                          onClick={() => setEditLabel(label.text)}
                          style={{
                            ...styles.presetLabelButton,
                            ...getColorStyle(label.color),
                            borderColor: editLabel === label.text ? '#3d4f4f' : 'transparent',
                          }}
                        >
                          {label.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Create New Label Button */}
                <button
                  onClick={() => setShowCreateLabelModal(true)}
                  style={{
                    marginTop: '16px',
                    padding: '10px 16px',
                    borderRadius: '10px',
                    border: '2px dashed rgba(124, 152, 133, 0.3)',
                    background: 'transparent',
                    color: '#5a7070',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    width: '100%',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s ease',
                  }}
                >
                  ➕ Create New Custom Label
                </button>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Notes (optional)</label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add any notes about this candidate..."
                  style={styles.formTextarea}
                  maxLength={500}
                />
              </div>
            </div>
            
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowLabelModal(false)}
                style={{ ...styles.actionButton, ...styles.editLabelButton }}
              >
                Cancel
              </button>
              <button
                onClick={saveLabelAndNotes}
                disabled={saving}
                style={{ ...styles.actionButton, ...styles.viewCvButton }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* CV Viewer Modal */}
      {showCvModal && selectedCandidate && (
        <div style={styles.modalOverlay} onClick={() => { setShowCvModal(false); setCvUrl(null); }}>
          <div style={{ ...styles.modal, maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {selectedCandidate.candidateData?.firstName}'s CV
              </h2>
              <button
                style={styles.closeButton}
                onClick={() => { setShowCvModal(false); setCvUrl(null); }}
              >
                ✕
              </button>
            </div>
            
            <div style={styles.modalBody}>
              {cvUrl ? (
                <iframe
                  src={cvUrl}
                  style={styles.cvViewer}
                  title="CV Viewer"
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b8080' }}>
                  Loading CV...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Create Custom Label Modal */}
      {showCreateLabelModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateLabelModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Create Custom Label</h2>
              <button
                style={styles.closeButton}
                onClick={() => setShowCreateLabelModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Label Text</label>
                <input
                  type="text"
                  value={newLabelText}
                  onChange={(e) => setNewLabelText(e.target.value)}
                  placeholder="e.g., Second Interview, Technical Review..."
                  style={styles.formInput}
                  maxLength={50}
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Color</label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setNewLabelColor(color.value)}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '10px',
                        border: newLabelColor === color.value ? '2px solid #3d4f4f' : '2px solid transparent',
                        background: color.bg,
                        color: color.text,
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {color.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Preview */}
              {newLabelText && (
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Preview</label>
                  <div>
                    <span style={{
                      ...styles.labelBadge,
                      ...getColorStyle(newLabelColor),
                    }}>
                      🏷️ {newLabelText}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Existing Custom Labels */}
              {customLabels.length > 0 && (
                <div style={{ ...styles.formGroup, marginTop: '24px', paddingTop: '20px', borderTop: '1px solid rgba(124, 152, 133, 0.15)' }}>
                  <label style={styles.formLabel}>Your Custom Labels ({customLabels.length}/20)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {customLabels.map((label) => (
                      <div
                        key={label.text}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 10px',
                          borderRadius: '8px',
                          ...getColorStyle(label.color),
                          fontSize: '13px',
                        }}
                      >
                        <span>{label.text}</span>
                        <button
                          onClick={() => deleteCustomLabel(label.text)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            fontSize: '14px',
                            opacity: 0.6,
                            lineHeight: 1,
                          }}
                          title="Delete label"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowCreateLabelModal(false)}
                style={{ ...styles.actionButton, ...styles.editLabelButton }}
              >
                Cancel
              </button>
              <button
                onClick={createCustomLabel}
                disabled={creatingLabel || !newLabelText.trim()}
                style={{ 
                  ...styles.actionButton, 
                  ...styles.viewCvButton,
                  opacity: (!newLabelText.trim() || creatingLabel) ? 0.6 : 1,
                }}
              >
                {creatingLabel ? 'Creating...' : 'Create Label'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectModal && rejectingCandidate && (
        <div style={styles.modalOverlay} onClick={() => setShowRejectModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Confirm Rejection</h2>
              <button
                style={styles.closeButton}
                onClick={() => setShowRejectModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <p style={{ marginBottom: '20px', lineHeight: '1.6' }}>
                Are you sure you want to send a rejection email to{' '}
                <strong>
                  {rejectingCandidate.candidateData?.firstName} {rejectingCandidate.candidateData?.lastName}
                </strong>
                {' '}at{' '}
                <strong>{rejectingCandidate.candidateData?.email}</strong>?
              </p>
              
              <div style={{
                background: 'rgba(239, 68, 68, 0.08)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid rgba(239, 68, 68, 0.2)',
              }}>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#b91c1c',
                  margin: 0,
                }}>
                  ⚠️ This action cannot be undone. The candidate will receive your rejection email immediately.
                </p>
              </div>
            </div>
            
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowRejectModal(false)}
                style={{ ...styles.actionButton, ...styles.editLabelButton }}
                disabled={sendingRejection}
              >
                Cancel
              </button>
              <button
                onClick={sendRejectionEmail}
                disabled={sendingRejection}
                style={{ 
                  ...styles.actionButton, 
                  ...styles.rejectButton,
                  opacity: sendingRejection ? 0.6 : 1,
                }}
              >
                {sendingRejection ? 'Sending...' : '✕ Send Rejection Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CandidatesPage;